import {Column, Group, TID} from "./Table";
import {cellToString} from "./TableCsvExport";

// matches the min width enforced by the drag handle
const minColumnWidth = 50;

// the resize divider sits beside the header cell and shares the column width
const headerDividerWidth = 4;
const headerIconWidth = 18;

// rounding slack so sub-pixel text widths never wrap
const slack = 1;

// autoSizeColumnWidth returns the column width that fits the widest row text
// across all groups (not only the rows currently scrolled into view) and the
// header label. Text is laid out in a hidden element that carries the real
// cell/header class so font, letter-spacing, padding and borders match.
// A cell whose content is a component (e.g. icon buttons) cannot be measured as
// text, so its rendered cells in the table body are measured instead.
export function autoSizeColumnWidth<T extends TID>(props: {
    groups: Group<T>[];
    column: Column<T>;
    cellClassName: string;
    headerEl: HTMLElement | null;
    headerIcons: number;
}): number {
    const host = props.headerEl?.parentElement || document.body;

    const lines: string[] = [];
    let opaque = false;
    props.groups.map(g => g.rows.map(row => {
        cellToString(row, props.column).split("\n").map(line => lines.push(line));
        if(!opaque && hasOpaqueContent(props.column.render(row, props.column))) {
            opaque = true;
        }
        return null;
    }));

    let width = measureLines(host, props.cellClassName, lines);
    if(opaque) {
        const rendered = measureRenderedCells(props.headerEl, props.column.name);
        width = Math.max(width, rendered === null ? props.column.width : rendered);
    }

    if(props.headerEl) {
        const headerWidth = props.column.renderName
            ? Math.max(...measureNaturalWidths([props.headerEl])) + headerDividerWidth
            : measureLines(host, props.headerEl.className, [props.column.name])
                + headerDividerWidth + props.headerIcons * headerIconWidth;
        width = Math.max(width, headerWidth);
    }

    return Math.max(width + slack, minColumnWidth);
}

// hasOpaqueContent reports whether a rendered cell contains a component
// element, whose visible output cannot be read as text
function hasOpaqueContent(node: any): boolean {
    if(node === null || node === undefined || typeof node !== "object") return false;
    if(Array.isArray(node)) return node.some(hasOpaqueContent);
    if(typeof node.type === "function" || (typeof node.type === "object" && node.type !== null)) return true;
    return hasOpaqueContent(node.props?.children);
}

// measureRenderedCells returns the widest natural width of the column's cells
// currently rendered in the table body, or null when none are rendered
function measureRenderedCells(headerEl: HTMLElement | null, columnName: string): number | null {
    const root = headerEl?.closest("[data-table-root]");
    if(!root) return null;

    const cells = Array.from(root.querySelectorAll<HTMLElement>(`[data-col="${CSS.escape(columnName)}"]`));
    if(cells.length === 0) return null;

    return Math.max(...measureNaturalWidths(cells));
}

// measureNaturalWidths returns the border-box width each element takes when it
// shrinks to fit its content; the inline sizing is restored afterwards
function measureNaturalWidths(els: HTMLElement[]): number[] {
    const saved = els.map(el => ({
        width: el.style.width,
        minWidth: el.style.minWidth,
        maxWidth: el.style.maxWidth,
        flex: el.style.flex,
    }));

    els.forEach(el => {
        el.style.width = "max-content";
        el.style.minWidth = "0";
        el.style.maxWidth = "none";
        el.style.flex = "none";
    });

    try {
        return els.map(el => Math.ceil(el.getBoundingClientRect().width));
    } finally {
        els.forEach((el, i) => Object.assign(el.style, saved[i]));
    }
}

// measureLines returns the border-box width of the widest line when laid out
// without wrapping inside an element with the given class
function measureLines(host: HTMLElement, className: string, lines: string[]): number {
    const el = document.createElement("div");
    el.className = className;
    el.style.position = "absolute";
    el.style.visibility = "hidden";
    el.style.pointerEvents = "none";
    el.style.width = "max-content";
    el.style.minWidth = "0";
    el.style.maxWidth = "none";
    el.style.flex = "none";
    el.style.display = "block";
    el.style.whiteSpace = "pre";

    lines.map(line => {
        const div = document.createElement("div");
        div.textContent = line;
        el.appendChild(div);
        return null;
    });

    host.appendChild(el);
    try {
        return Math.ceil(el.getBoundingClientRect().width);
    } finally {
        host.removeChild(el);
    }
}

// changedColumnNames returns the names of the columns whose text differs
// between a row before and after an edit. A new row (prev null) changes every
// column that has text.
export function changedColumnNames<T extends TID>(columns: Column<T>[], edits: {prev: T | null; next: T}[]): string[] {
    return columns
        .filter(c => edits.some(e => cellToString(e.next, c) !== (e.prev ? cellToString(e.prev, c) : "")))
        .map(c => c.name);
}
